import React, { useEffect, useRef } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Reusable confirmation dialog. Renders when `open`.
 * Ergonomics:
 *  - Auto-focuses the primary confirm button when the dialog opens
 *  - Pressing Enter triggers `onConfirm` (unless focus is inside a
 *    <textarea> or a form control that needs Enter for its own behaviour)
 *  - Pressing Escape triggers `onCancel`
 *  - Listeners are attached only while `open` is true and torn down on
 *    close/unmount, so there is no risk of double-firing
 */
export default function ConfirmDialog({
  open, onCancel, onConfirm, title, message, confirmLabel = "Confirm",
  cancelLabel = "Cancel", tone = "primary", testId = "confirm-dialog", loading
}) {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    // Auto-focus the primary confirm button so Enter/Space fires it directly.
    // A short timeout lets the button mount before we focus.
    const focusTimer = window.setTimeout(() => {
      confirmRef.current?.focus();
    }, 30);

    const onKey = (e) => {
      if (loading) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (e.key === "Enter" && tag !== "textarea") {
        e.preventDefault();
        onConfirm?.();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onCancel?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onConfirm, onCancel, loading]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6 no-print"
      onClick={onCancel} data-testid={testId} role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-md border border-[#E5D8C4] shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-4 p-6">
          <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center ${
            tone === "danger" ? "bg-red-50 text-red-700" : "bg-[#F3ECDF] text-[#8B6F47]"}`}>
            <AlertTriangle size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg text-[#2D2622]">{title}</div>
            <div className="text-sm text-[#5D5148] mt-2">{message}</div>
          </div>
          <button onClick={onCancel} className="text-[#8B6F47] hover:text-[#2D2622]" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="flex justify-end gap-2 p-4 bg-[#FAF7F2] border-t border-[#F3ECDF]">
          <button onClick={onCancel} data-testid={`${testId}-cancel`}
            className="h-10 px-5 text-xs uppercase tracking-widest border border-[#E5D8C4] bg-white hover:border-[#2D2622]">
            {cancelLabel}
          </button>
          <button ref={confirmRef} onClick={onConfirm} disabled={loading}
            data-testid={`${testId}-confirm`} autoFocus
            className={`h-10 px-5 text-xs uppercase tracking-widest text-white disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8B6F47] ${
              tone === "danger" ? "bg-red-700 hover:bg-red-800" : "bg-[#2D2622] hover:bg-[#5D4A2E]"}`}>
            {loading ? "..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
